import { TIMELINE_RULER_TICK_WIDTH_PX } from "../../../../../config/config";

const TICK_LINE_WIDTH = 1;

const MAJOR_TICK_VERTICAL_OFFSET = 20;
const MAJOR_TICK_HEIGHT_PX = 12;

const TIME_LABEL_VERTICAL_OFFSET = 14;

const DESIRED_MAJOR_TICK_PX = 100;
const MIN_MAJOR_TICK_PX = 70;
const MAX_MAJOR_TICK_PX = 140;

const MAJOR_TICK_CANDIDATES_SEC = [
    0.001, 0.002, 0.005,
    0.01, 0.02, 0.05,
    0.1, 0.2, 0.25, 0.5,
    1, 2, 5,
    10, 15, 30,
    60, 120, 300, 600, 900, 1800,
    3600, 7200, 14400,
];

function pickMajorTickSec(pxPerSec: number): number {
    if (!Number.isFinite(pxPerSec) || pxPerSec <= 0) return 1;
    let best = MAJOR_TICK_CANDIDATES_SEC[0] ?? 1;
    let bestScore = Number.POSITIVE_INFINITY;

    for (const sec of MAJOR_TICK_CANDIDATES_SEC) {
        const px = sec * pxPerSec;
        const inRange = px >= MIN_MAJOR_TICK_PX && px <= MAX_MAJOR_TICK_PX;
        const score = inRange ? Math.abs(px - DESIRED_MAJOR_TICK_PX) : Math.abs(px - DESIRED_MAJOR_TICK_PX) + 1_000;
        if (score < bestScore) {
            bestScore = score;
            best = sec;
        }
    }

    return best;
}

function decimalsForStep(stepSec: number): number {
    if (!Number.isFinite(stepSec) || stepSec <= 0) return 0;
    for (let dec = 0; dec <= 3; dec++) {
        const scaled = stepSec * Math.pow(10, dec);
        if (Math.abs(scaled - Math.round(scaled)) < 1e-9) return dec;
    }
    return 3;
}

function formatRulerTime(seconds: number, decimals: number): string {
    const sign = seconds < 0 ? "-" : "";
    const abs = Math.abs(seconds);

    const scale = Math.pow(10, Math.max(0, Math.min(3, decimals)));
    const scaledTotalSeconds = Math.round(abs * scale);

    const wholeSeconds = Math.floor(scaledTotalSeconds / scale);
    const fracUnits = scaledTotalSeconds % scale; // 0..scale-1

    const hours = Math.floor(wholeSeconds / 3600);
    const minutes = Math.floor((wholeSeconds % 3600) / 60);
    const secsInt = wholeSeconds % 60;

    const secsBase = secsInt.toString().padStart(2, "0");
    const secStr =
        decimals > 0
            ? `${secsBase}.${fracUnits.toString().padStart(decimals, "0")}`
            : secsBase;

    if (hours > 0) return `${sign}${hours}:${minutes.toString().padStart(2, "0")}:${secStr}`;
    return `${sign}${minutes}:${secStr}`;
}

export const drawCanvasTimelineRuler = (
    context: CanvasRenderingContext2D,
    duration: number,
    options: {
        scrollOffset: number;
        fillColor: string;
        zoom: number;
        leftPadSeconds?: number;
        baseStartSec?: number;
    },
) => {
    const height = context.canvas.height / window.devicePixelRatio;
    const width = context.canvas.width / window.devicePixelRatio;

    const pxPerSec = TIMELINE_RULER_TICK_WIDTH_PX * options.zoom;
    const majorTickSec = pickMajorTickSec(pxPerSec);
    const majorTickPx = majorTickSec * pxPerSec;
    const labelDecimals = decimalsForStep(majorTickSec);

    const leftPadSeconds = options.leftPadSeconds ?? 0;
    const leftPadPx = leftPadSeconds * pxPerSec;
    const effectiveScroll = options.scrollOffset - leftPadPx;
    const baseStartSec = options.baseStartSec ?? 0;

    initCanvas(context, width, height, { fillColor: options.fillColor, scrollOffset: effectiveScroll });

    // Determine visible absolute time range.
    const visibleStartSec = baseStartSec + (effectiveScroll / pxPerSec);
    const visibleEndSec = baseStartSec + ((effectiveScroll + width) / pxPerSec);

    // Major ticks aligned to absolute time (multiples of majorTickSec from 0:00).
    const firstMajorTickSec = Math.floor(visibleStartSec / majorTickSec) * majorTickSec;
    const lastMajorTickSec = Math.ceil(visibleEndSec / majorTickSec) * majorTickSec;

    // Draw major ticks only (no minor ticks).
    context.beginPath();

    for (let t = firstMajorTickSec; t <= lastMajorTickSec + 1e-9; t += majorTickSec) {
        const xPx = (t - baseStartSec) * pxPerSec;
        context.moveTo(xPx, MAJOR_TICK_VERTICAL_OFFSET);
        context.lineTo(xPx, MAJOR_TICK_VERTICAL_OFFSET + MAJOR_TICK_HEIGHT_PX);
    }

    context.stroke();

    // Draw labels on major ticks
    for (let t = firstMajorTickSec; t <= lastMajorTickSec + 1e-9; t += majorTickSec) {
        const xPx = (t - baseStartSec) * pxPerSec;

        // Only label ticks in the visible canvas area (small overscan)
        if (xPx < effectiveScroll - majorTickPx || xPx > effectiveScroll + width + majorTickPx) continue;

        const formattedTime = formatRulerTime(t, labelDecimals);

        // Pick an alignment based on the position
        let timeLabelAlign: CanvasTextAlign = "center";
        if (Math.abs(t) < 1e-9) {
            timeLabelAlign = "left";
        } else if (
            (t > duration || Math.abs(t - duration) < 0.2) &&
            xPx - options.scrollOffset > width - 15.0
        ) {
            timeLabelAlign = "right";
        }

        drawTimeLabel(context, xPx, formattedTime, timeLabelAlign);
    }

    context.restore();
};

const initCanvas = (
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    options: { fillColor: string; scrollOffset: number },
) => {
    context.clearRect(0, 0, width, height);
    context.strokeStyle = options.fillColor;
    context.fillStyle = options.fillColor;
    context.lineWidth = TICK_LINE_WIDTH;
    context.textAlign = "center";

    context.save();
    context.translate(-options.scrollOffset, 0);
};

const drawTimeLabel = (context: CanvasRenderingContext2D, x: number, time: string, align: CanvasTextAlign) => {
    context.textAlign = align;
    context.fillText(time, x, TIME_LABEL_VERTICAL_OFFSET);
};
